import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { Clock, Calendar } from 'lucide-react';
interface SetFollowUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: Candidate | null;
  onSave: (candidate: Candidate) => void;
}
const REASON_PRESETS = ['Check in on counter-offer', 'Revisit in next quarter', 'See if new role works out', 'Follow up on referral', 'Custom reason…'];
export function SetFollowUpModal({
  isOpen,
  onClose,
  candidate,
  onSave
}: SetFollowUpModalProps) {
  const {
    addToast
  } = useToast();
  const [date, setDate] = useState('');
  const [reasonPreset, setReasonPreset] = useState<string>(REASON_PRESETS[0]);
  const [customReason, setCustomReason] = useState('');
  const [moveToFollowUp, setMoveToFollowUp] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    if (isOpen && candidate) {
      setDate(candidate.followUpDate || '');
      const existing = candidate.followUpReason || '';
      if (existing && !REASON_PRESETS.includes(existing)) {
        setReasonPreset('Custom reason…');
        setCustomReason(existing);
      } else {
        setReasonPreset(existing || REASON_PRESETS[0]);
        setCustomReason('');
      }
      setMoveToFollowUp(candidate.status !== 'Follow Up Later');
      setError('');
    }
  }, [isOpen, candidate]);
  if (!candidate) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) {
      setError('Please choose a follow-up date');
      return;
    }
    const reason = reasonPreset === 'Custom reason…' ? customReason.trim() : reasonPreset;
    if (!reason) {
      setError('Please add a reason');
      return;
    }
    const updated: Candidate = {
      ...candidate,
      followUpDate: date,
      followUpReason: reason,
      status: moveToFollowUp ? 'Follow Up Later' : candidate.status
    };
    onSave(updated);
    addToast({
      type: 'success',
      title: 'Follow-up scheduled',
      message: `Reminder set for ${new Date(date).toLocaleDateString()}.`
    });
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Set Follow-up" maxWidth="lg" data-id="element-1201">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1202">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50/60 border border-orange-100" data-id="element-1203">
          <div className="h-10 w-10 rounded-full bg-white border border-orange-100 flex items-center justify-center text-orange-600" data-id="element-1204">
            <Clock className="h-5 w-5" data-id="element-1205" />
          </div>
          <div data-id="element-1206">
            <p className="text-sm font-semibold text-gray-900" data-id="element-1207">
              {candidate.firstName} {candidate.lastName}
            </p>
            <p className="text-xs text-gray-500" data-id="element-1208">
              {candidate.role} • {candidate.status}
            </p>
          </div>
        </div>

        <div data-id="element-1209">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1210">
            Follow-up date & time
          </label>
          <div className="relative" data-id="element-1211">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" data-id="element-1212" />
            <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" data-id="element-1213" />
          </div>
        </div>

        <div data-id="element-1214">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1215">
            Reason
          </label>
          <div className="grid grid-cols-1 gap-2" data-id="element-1216">
            {REASON_PRESETS.map(preset => <button key={preset} type="button" onClick={() => setReasonPreset(preset)} className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${reasonPreset === preset ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`} data-id="element-1217">
                {preset}
              </button>)}
          </div>
          {reasonPreset === 'Custom reason…' && <textarea value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="e.g. Reach out about the new opening on the East team" rows={3} className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none" data-id="element-1218" />}
        </div>

        {candidate.status !== 'Follow Up Later' && <label className="flex items-center gap-2 cursor-pointer" data-id="element-1219">
            <input type="checkbox" checked={moveToFollowUp} onChange={e => setMoveToFollowUp(e.target.checked)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-id="element-1220" />
            <span className="text-sm text-gray-700" data-id="element-1221">
              Move candidate to{' '}
              <span className="font-medium" data-id="element-1222">Follow Up Later</span>
            </span>
          </label>}

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg" data-id="element-1223">
            {error}
          </p>}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100" data-id="element-1224">
          <Button type="button" variant="secondary" onClick={onClose} data-id="element-1225">
            Cancel
          </Button>
          <Button type="submit" variant="primary" leftIcon={<Clock className="h-4 w-4" data-id="element-1227" />} data-id="element-1226">
            Save Follow-up
          </Button>
        </div>
      </form>
    </Modal>;
}